// BULK-03: calculated fields (a formula set once in the template) and number
// formats. The server checks and works out the formulas (document/formulas.py);
// these helpers only shape what the editors send and show.

export type NumberFormatSpec = {
    kind: "number" | "words";
    style: "indian" | "international" | "plain";
    decimals?: number;
    currency?: string | null;
};

/** The "Number format" choices in the field settings. */
export const NUMBER_FORMAT_PRESETS = [
    { value: "", label: "None" },
    { value: "number:indian", label: "Indian (12,00,000)" },
    { value: "number:international", label: "International (1,200,000)" },
    { value: "words:indian", label: "Amount in words (Indian)" },
    { value: "words:international", label: "Amount in words (International)" },
];

export const CURRENCIES = [
    { value: "", label: "None" },
    { value: "INR", label: "₹ INR" },
    { value: "USD", label: "$ USD" },
    { value: "EUR", label: "€ EUR" },
    { value: "GBP", label: "£ GBP" },
];

/** A saved spec (JSON string, object or nothing) as an object, or null. */
export const parseNumberFormat = (raw: any): NumberFormatSpec | null => {
    if (raw === undefined || raw === null || raw === "") return null;
    let spec = raw;
    try {
        // JSONString can arrive double-encoded.
        while (typeof spec === "string") spec = JSON.parse(spec);
    } catch (e) {
        return null;
    }
    return spec && typeof spec === "object" && spec.kind ? spec : null;
};

/** The preset value ("number:indian" …) a spec matches, "" for none. */
export const presetOf = (spec: NumberFormatSpec | null) =>
    spec ? `${spec.kind}:${spec.style === "plain" ? "international" : spec.style}` : "";

/** The spec for a preset, keeping the currency and decimals already chosen. */
export const specFor = (
    preset: string,
    currency: string | null = null,
    decimals: number = 0
): NumberFormatSpec | null => {
    if (!preset) return null;
    const [kind, style] = preset.split(":") as any;
    return { kind, style, decimals: Number(decimals) || 0, currency: currency || null };
};

/** What the save sends for `numberFormat`: a JSON string, or null to clear. */
export const numberFormatInput = (spec: any) => {
    const parsed = parseNumberFormat(spec);
    return parsed ? JSON.stringify(parsed) : null;
};

/** What the save sends for `formula`: the trimmed formula, or null to clear. */
export const formulaInput = (formula: any) => {
    const text = typeof formula === "string" ? formula.trim() : "";
    return text ? text : null;
};

/** The labels of the other text fields, for the formula chips. */
export const otherFieldLabels = (items: any[], index: number) =>
    Array.from(
        new Set(
            items
                .filter((item, i) => i !== index && item?.kind === 1 && item?.label)
                .map((item) => String(item.label).trim())
                .filter(Boolean)
        )
    );

/**
 * BULK-03 bulk preview: the formula inputs that need a value for everyone:
 * the ones the file doesn't have (a row-1 problem names them) and the ones
 * that already have a shared value (so it can be changed).
 */
export const inputsNeedingValues = (
    inputColumns: string[] = [],
    sharedValues: { [name: string]: any } = {},
    rowErrors: { row?: any; column?: any }[] = []
) => {
    const missing = new Set(
        (rowErrors || [])
            .filter((e) => e && Number(e.row) === 1 && e.column)
            .map((e) => String(e.column))
    );
    return (inputColumns || []).filter(
        (name) =>
            missing.has(name) ||
            Object.prototype.hasOwnProperty.call(sharedValues || {}, name)
    );
};
